import type { FilterNode, Sort, Task, UserSummary } from "@estelle/domain";
import { isCondition, isGroup, LIST_LIMITS } from "@estelle/domain";

import { projectTask } from "@/modules/tasks/projections";

/**
 * As many tasks as one request may ask for: the tasks route's own maximum
 * (`LIST_LIMITS`), which is also `MAX_LIMIT` in `adapters/fixture/query.ts`, so
 * one request holds the whole ~35-task dataset. `TaskList.tsx` and
 * `TaskBoard.tsx` both fetch everything and filter, sort and (for the list)
 * paginate client-side rather than forwarding to `TasksRepository.list` —
 * see the `overdue` comment in `fields.ts` for why a server-side filter
 * cannot evaluate every field this module declares.
 *
 * The API refuses a `limit` above that maximum, so this is the constant itself
 * and not a copy of its value.
 */
export const TASK_FETCH_LIMIT = LIST_LIMITS.tasks.max;

/**
 * Whether a task answers the typed search (#614): the term appears, in any
 * case, in its reference, its title, a tag or the assignee's name.
 *
 * The lists fetch every task and narrow client-side (see `TASK_FETCH_LIMIT`),
 * so this is where the search box does its work. The assignee is matched by
 * the name the list shows, through the same directory of active people: a
 * suspended or invited assignee shows as "Inactive user", and their name is not
 * searchable either, as on the server. An empty term matches everything.
 */
export function matchesTaskSearch(
  task: Task,
  term: string | undefined,
  users: readonly UserSummary[],
): boolean {
  const needle = (term ?? "").trim().toLowerCase();
  if (needle === "") return true;
  const assignee = users.find((user) => user.id === task.assigneeUserId);
  return [task.reference, task.title, ...task.tags, assignee?.name ?? ""].some(
    (text) => text.toLowerCase().includes(needle),
  );
}

function comparable(value: unknown): string | number {
  if (typeof value === "number") return value;
  if (typeof value === "string") return value;
  return "";
}

/**
 * Sort tasks against the shared `Sort[]` the toolbar produces.
 *
 * There is no repository call for this to forward to once filtering has
 * already moved client-side — the same reasoning `sortInTransitLines`
 * documents in `modules/warehouses/in-transit.ts`. Falls back to `dueDate`
 * ascending, the fixture adapter's own default, so a screen showing every
 * task client-filtered still opens in the order `all` would show with an
 * empty filter.
 */
export function sortTasks(
  tasks: readonly Task[],
  sort: readonly Sort[],
): Task[] {
  const effective: readonly Sort[] =
    sort.length > 0 ? sort : [{ column: "dueDate", direction: "asc" }];

  return [...tasks].sort((a, b) => {
    const pa = projectTask(a);
    const pb = projectTask(b);
    for (const { column, direction } of effective) {
      const left = comparable(pa[column]);
      const right = comparable(pb[column]);
      // A task with no value in the column is last whichever way it sorts, as
      // the API's `nulls last` has it (#542): no due date is not "earliest".
      if (left === "" || right === "") {
        if (left === right) continue;
        return left === "" ? 1 : -1;
      }
      const result =
        typeof left === "number" && typeof right === "number"
          ? left - right
          : String(left).localeCompare(String(right));
      if (result !== 0) return direction === "asc" ? result : -result;
    }
    return 0;
  });
}

/** FR-TSK-15: assigned to *or* watched by the current user, across every project. */
export function isMineOrWatched(task: Task, currentUserId: string): boolean {
  return (
    task.assigneeUserId === currentUserId ||
    task.watcherUserIds.includes(currentUserId)
  );
}

/**
 * The project a `projectId is <id>` condition narrows the current filter to,
 * at any depth — what lets "New task" on a project-filtered view (#399,
 * bullet 3) preselect that project instead of asking someone to pick again
 * the one they already filtered to. `undefined` when the filter names no
 * single project, which is the ordinary case everywhere but there.
 */
export function standingProjectId(filter: FilterNode): string | undefined {
  if (isCondition(filter)) {
    return filter.field === "projectId" &&
      filter.operator === "is" &&
      typeof filter.value === "string"
      ? filter.value
      : undefined;
  }
  if (isGroup(filter)) {
    for (const child of filter.children) {
      const found = standingProjectId(child);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}
